const KEY = "factscan:pendingUpgrade";
const TTL_MS = 20 * 60 * 1000; // 20 minutes

const LAST_INTENT_KEY = "factscan:lastUpgradeIntent";
const LAST_INTENT_TTL_MS = 60 * 60 * 1000; // 60 minutes — covers Stripe round-trip

export type PendingPlan = "pro" | "proplus";

export interface PendingIntent {
  tool_type?: string;
  source_page?: string;
  paywall_copy_variant?: string;
}

interface PendingUpgrade {
  plan: PendingPlan;
  ts: number;
  tool_type?: string;
  source_page?: string;
  paywall_copy_variant?: string;
}

// Whitelist enums — anything outside the closed sets is dropped so
// no file name, OCR text, extracted field, or document content can
// leak into storage or analytics via these props.
const TOOL_TYPES = new Set(["invoice", "bank-statement", "pdf-ocr"]);
const COPY_VARIANTS = new Set(["invoice", "bank-statement", "pdf-ocr", "default"]);
const SOURCE_PAGE_RE = /^[a-z0-9-]{1,80}$/;

function sanitizeToolType(value: unknown): string | undefined {
  return typeof value === "string" && TOOL_TYPES.has(value) ? value : undefined;
}
function sanitizeCopyVariant(value: unknown): string | undefined {
  return typeof value === "string" && COPY_VARIANTS.has(value) ? value : undefined;
}
function sanitizeSourcePage(value: unknown): string | undefined {
  return typeof value === "string" && SOURCE_PAGE_RE.test(value) ? value : undefined;
}

export function setPendingUpgrade(plan: PendingPlan, intent?: PendingIntent): void {
  try {
    const payload: PendingUpgrade = { plan, ts: Date.now() };
    const tool = sanitizeToolType(intent?.tool_type);
    const src = sanitizeSourcePage(intent?.source_page);
    const variant = sanitizeCopyVariant(intent?.paywall_copy_variant);
    if (tool) payload.tool_type = tool;
    if (src) payload.source_page = src;
    if (variant) payload.paywall_copy_variant = variant;
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // localStorage unavailable (private mode, quota) — silently ignore
  }
}

export function getPendingUpgrade(): PendingUpgrade | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingUpgrade>;
    if (
      !parsed ||
      (parsed.plan !== "pro" && parsed.plan !== "proplus") ||
      typeof parsed.ts !== "number"
    ) {
      localStorage.removeItem(KEY);
      return null;
    }
    if (Date.now() - parsed.ts > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    const result: PendingUpgrade = { plan: parsed.plan, ts: parsed.ts };
    const tool = sanitizeToolType(parsed.tool_type);
    const src = sanitizeSourcePage(parsed.source_page);
    const variant = sanitizeCopyVariant(parsed.paywall_copy_variant);
    if (tool) result.tool_type = tool;
    if (src) result.source_page = src;
    if (variant) result.paywall_copy_variant = variant;
    return result;
  } catch {
    return null;
  }
}

export function clearPendingUpgrade(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Short-lived "last upgrade intent" — written right before Stripe redirect,
// read on /pricing?success=true to attribute purchase_success to the
// originating paywall variant. Strict plan-match enforced by the reader.
// ---------------------------------------------------------------------------

export interface LastUpgradeIntent {
  plan: PendingPlan;
  ts: number;
  tool_type?: string;
  source_page?: string;
  paywall_copy_variant?: string;
}

export function setLastUpgradeIntent(plan: PendingPlan, intent?: PendingIntent): void {
  try {
    const payload: LastUpgradeIntent = { plan, ts: Date.now() };
    const tool = sanitizeToolType(intent?.tool_type);
    const src = sanitizeSourcePage(intent?.source_page);
    const variant = sanitizeCopyVariant(intent?.paywall_copy_variant);
    if (tool) payload.tool_type = tool;
    if (src) payload.source_page = src;
    if (variant) payload.paywall_copy_variant = variant;
    localStorage.setItem(LAST_INTENT_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

export function getLastUpgradeIntent(): LastUpgradeIntent | null {
  try {
    const raw = localStorage.getItem(LAST_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LastUpgradeIntent>;
    if (
      !parsed ||
      (parsed.plan !== "pro" && parsed.plan !== "proplus") ||
      typeof parsed.ts !== "number"
    ) {
      localStorage.removeItem(LAST_INTENT_KEY);
      return null;
    }
    if (Date.now() - parsed.ts > LAST_INTENT_TTL_MS) {
      localStorage.removeItem(LAST_INTENT_KEY);
      return null;
    }
    const result: LastUpgradeIntent = { plan: parsed.plan, ts: parsed.ts };
    const tool = sanitizeToolType(parsed.tool_type);
    const src = sanitizeSourcePage(parsed.source_page);
    const variant = sanitizeCopyVariant(parsed.paywall_copy_variant);
    if (tool) result.tool_type = tool;
    if (src) result.source_page = src;
    if (variant) result.paywall_copy_variant = variant;
    return result;
  } catch {
    return null;
  }
}

export function clearLastUpgradeIntent(): void {
  try {
    localStorage.removeItem(LAST_INTENT_KEY);
  } catch {
    // ignore
  }
}